import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { User } from 'oidc-client-ts';
import { isNullOrEmpty } from '../utils/strings';
import {
  canRenewNow,
  INTERACTIVE_RENEW_TIMEOUT_MS,
  isSessionDead,
  notifySessionDead,
  renewSessionWithin,
  startSessionRecovery
} from './session-recovery';

/**
 * Marca de «esta peticion ya se reintento despues de renovar». Sin ella, un 401 que persiste
 * despues de un token nuevo -- permisos, no caducidad -- daria vueltas para siempre.
 */
const RETRIED_AFTER_RENEW = '__j2lRetriedAfterRenew';

type RetryableConfig = InternalAxiosRequestConfig & { [RETRIED_AFTER_RENEW]?: boolean };

/**
 * Las rutas de IdentityServer se quedan fuera: renovar por un 401 de la propia renovacion es la
 * receta del bucle.
 *
 * Se compara contra el `authority` ENTERO, no contra su origen ni contra un `/connect/` suelto. El
 * origen no basta porque en prepro y produccion identity y la aplicacion cuelgan del mismo
 * (`{BaseUrl}/identity` y `{BaseUrl}/website`), y `/connect/` a secas excluiria en silencio un
 * endpoint de negocio que lo llevase en la ruta.
 */
const isIdentityEndpoint = (url: string | undefined, authority: string): boolean => {
  if (url == null) return false;
  try {
    const target = new URL(url, axios.defaults.baseURL || window.location.origin);
    const identity = new URL(authority);
    return target.href.startsWith(`${identity.origin}${identity.pathname.replace(/\/$/, '')}/connect/`);
  } catch {
    return /\/connect\//.test(url);
  }
};

/** Si la peticion viajaba autenticada. Un 401 sin cabecera es un 401 esperado, no una sesion caida. */
const wasAuthenticated = (config: InternalAxiosRequestConfig): boolean => {
  const headers = config.headers as unknown as { get?: (name: string) => unknown; Authorization?: unknown } | undefined;
  if (headers == null) return false;
  return (typeof headers.get === 'function' ? headers.get('Authorization') : headers.Authorization) != null;
};

export class BadResponseFormatError extends Error {
  constructor(public response: AxiosResponse) {
    super('Malformed response');
  }
}

export const configAxios = (options: {
  baseUrl: string;
  useBearerToken?: {
    authority: string;
    clientId: string;
  };
  onHttp401Error?: (data: { response?: AxiosResponse; error?: AxiosError }) => void;
  acceptOnlyJsonResponses?: boolean;
}) => {
  const port = window.location.port === '80' || window.location.port === '443' ? '' : `:${window.location.port}`;
  const _baseUrl =
    (options.baseUrl || '').trim().length === 0 ? `${window.location.protocol}//${window.location.host}${port}` : (options.baseUrl || '').trim();
  axios.defaults.baseURL = _baseUrl;

  if (options.useBearerToken) {
    axios.interceptors.request.use((config) => {
      const oidcStorage = localStorage.getItem(`oidc.user:${options.useBearerToken!.authority}:${options.useBearerToken!.clientId}`);
      if (!oidcStorage) {
        return config;
      }
      const user = User.fromStorageString(oidcStorage);
      const token = user?.access_token;
      if (!token) return config;

      // Do something before request is sent
      config.headers.Authorization = `Bearer ${token}`;
      config.headers['lang'] = 'es';

      return config;
    });
  }
  /**
   * Red de seguridad del 401.
   *
   * Antes esto solo existia si el llamante pasaba `onHttp401Error`, y las tres aplicaciones lo
   * pasan a `undefined` fuera de `development`: en prepro y en produccion un 401 no lo recogia
   * nadie y el usuario se quedaba con el toast generico de error.
   *
   * Ahora un 401 intenta primero renovar y repetir la peticion. Ese reintento es lo que salva el
   * caso reportado: alguien que vuelve al dia siguiente, termina el formulario que tenia a medias y
   * lo envia. El token esta muerto, se renueva sobre la marcha y el envio pasa, sin que llegue a
   * ver un error. `onHttp401Error` se reserva ya solo para cuando la recuperacion no ha podido.
   */
  if (options?.useBearerToken != null || options?.onHttp401Error != null) {
    axios.interceptors.response.use(
      (v) => {
        if (v?.status === 401) options.onHttp401Error?.({ response: v });
        return v;
      },
      async (e) => {
        const error = e as AxiosError;
        if (error?.response?.status !== 401) return Promise.reject(e);

        const bearer = options?.useBearerToken;
        const config = error.config as RetryableConfig | undefined;

        const renewable =
          bearer != null &&
          config != null &&
          !config[RETRIED_AFTER_RENEW] &&
          wasAuthenticated(config) &&
          !isIdentityEndpoint(config.url, bearer.authority);

        if (renewable) {
          if (isSessionDead()) {
            // Ya se sabe que no hay nada que renovar. Se le vuelve a poner el aviso delante, que es
            // lo unico util que queda, sin gastar otro `signinSilent` condenado a fallar.
            notifySessionDead();
          } else if (!canRenewNow()) {
            // La renovacion acaba de fallar y la recuperacion de fondo ya esta en ello. Una pagina
            // que reintenta sus peticiones no debe convertir un chorro de 401 en un chorro de
            // `signinSilent`.
            startSessionRecovery();
          } else {
            const outcome = await renewSessionWithin(INTERACTIVE_RENEW_TIMEOUT_MS);

            if (outcome.ok) {
              config[RETRIED_AFTER_RENEW] = true;
              // El interceptor de peticion vuelve a leer el token de `localStorage`, que es donde
              // `signinSilent` acaba de dejar el nuevo. Se quita la cabecera anterior por si aquel
              // no llegase a reescribirla.
              const headers = config.headers as unknown as { delete?: (name: string) => void };
              if (typeof headers?.delete === 'function') headers.delete('Authorization');
              return axios(config);
            }

            // No se ha podido ahora, pero puede que si dentro de un rato. Se arma la recuperacion aunque
            // `silentRenewError` no haya llegado nunca, que es lo que pasa cuando el equipo ha estado
            // suspendido y los temporizadores de oidc-client-ts no han llegado a correr.
            if (!outcome.sessionDead) startSessionRecovery();
          }
        }

        options?.onHttp401Error?.({ error });
        return Promise.reject(e);
      }
    );
  }
  if (options?.acceptOnlyJsonResponses) {
    axios.interceptors.response.use((response: AxiosResponse) => {
      const ct = String(response?.headers?.['content-type'] || '').toLowerCase();
      if (!isNullOrEmpty(ct) && !ct.includes('application/json')) {
        throw new BadResponseFormatError(response);
      }

      return response;
    });
  }
};
